"use client";

import { useState } from "react";
import type { Theme, ColorPreset } from "@/lib/themes";

interface PasswordGateProps {
  slug: string;
  theme: Theme;
  preset: ColorPreset;
}

export default function PasswordGate({ slug, theme, preset }: PasswordGateProps) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const isDark = theme.id === "clean" || theme.id === "dark";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(false);
    setLoading(true);

    try {
      const res = await fetch(`/api/p/${slug}/unlock`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });

      if (res.ok) {
        window.location.reload();
      } else {
        setError(true);
        setLoading(false);
      }
    } catch {
      setError(true);
      setLoading(false);
    }
  };

  const cardBg =
    theme.id === "paper"
      ? "#FFFFFF"
      : theme.id === "clean"
        ? "rgba(26, 29, 37, 0.7)"
        : "rgba(18, 20, 24, 0.8)";

  const cardBorder =
    theme.id === "paper" ? "1px solid #E5E5E5" : "1px solid rgba(255,255,255,0.08)";

  const inputColor = isDark ? "#E0E0E0" : "#1A1A1A";
  const inputBg = isDark ? "rgba(255,255,255,0.06)" : "#F5F5F5";
  const inputBorder = isDark ? "rgba(255,255,255,0.1)" : "#E0E0E0";

  return (
    <div
      className={`published-page ${theme.bodyClass}`}
      style={{
        ["--link" as string]: preset.accent,
        ["--link-hover" as string]: preset.accentHover,
      } as React.CSSProperties}
    >
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: "var(--bg)" }}>
        <div
          className={`w-full max-w-[420px] py-12 px-10 rounded-lg border border-border text-center flex flex-col items-center gap-2 ${isDark ? "backdrop-blur-[20px] backdrop-saturate-[180%]" : ""}`}
          style={{ background: cardBg, border: cardBorder }}
        >
          {/* Lock icon */}
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--heading)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>

          <h1 className="text-xl font-bold mt-3 leading-tight" style={{ color: "var(--heading)" }}>
            This document is protected
          </h1>
          <p className="text-[0.9375rem] mb-4" style={{ color: "var(--muted)" }}>
            Enter the password to view
          </p>

          <form onSubmit={handleSubmit} className="w-full flex flex-col gap-3 mt-2">
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(false);
              }}
              placeholder="Password"
              className="w-full py-3 px-4 text-[0.9375rem] rounded-lg text-center outline-none transition-colors duration-200 border focus:border-[var(--heading)] box-border"
              style={{
                color: inputColor,
                background: inputBg,
                borderColor: inputBorder,
              }}
              autoFocus
            />
            <button
              type="submit"
              disabled={loading || !password}
              className="w-full py-3 px-6 text-[0.9375rem] font-semibold border-none rounded-lg cursor-pointer transition-all duration-200 hover:enabled:opacity-90 hover:enabled:-translate-y-px disabled:opacity-50 disabled:cursor-default"
              style={{ background: "var(--heading)", color: "var(--bg)" }}
            >
              {loading ? "Unlocking..." : "Unlock"}
            </button>
          </form>

          {error && (
            <p className="text-xs text-destructive mt-1 animate-[fadeIn_0.3s_ease-out]">
              Incorrect password
            </p>
          )}

          <footer className="mt-8 text-xs" style={{ color: "var(--muted)" }}>
            Published with{" "}
            <a
              className="no-underline font-bold hover:opacity-80 transition-opacity"
              href="/?ref=published&utm_source=pubmark&utm_medium=footer&utm_campaign=published_page"
              style={{ color: "var(--heading)" }}
            >
              Pub<span style={{ color: "var(--heading)" }}>mark</span>
            </a>
          </footer>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
